/**
 * Shared utilities for gallery components
 * Prevents code duplication across acrylic-depth-card.js and collection-museum-gallery.js
 */

(function () {
  'use strict';

  /**
   * Create a throttled requestAnimationFrame helper
   * Prevents multiple rapid updates by batching into single rAF frame
   * @param {Function} callback - Function to call on next frame
   * @returns {Function} Throttle function that returns early if already scheduled
   */
  function createRafThrottle(callback) {
    let ticking = false;
    return function throttledCallback() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        callback();
        ticking = false;
      });
    };
  }

  // Matches the /cart/add.js timeout used by ProductForm._cartFetchWithRetry in assets/theme.js
  const DEFAULT_FETCH_TIMEOUT_MS = 12000;

  /**
   * Fetch JSON with comprehensive error handling and an abort-based timeout.
   * A stalled request is aborted after timeoutMs so callers always get a rejection
   * instead of hanging forever with no error and no UI feedback.
   * @param {string} url - URL to fetch
   * @param {Object} [options] - Fetch options (optional `timeoutMs` override)
   * @returns {Promise<Object>} Parsed JSON response
   * @throws {Error} On HTTP error, parse error, timeout, or network failure
   */
  function fetchJson(url, options) {
    const opts = options || {};
    const timeoutMs =
      typeof opts.timeoutMs === 'number' ? opts.timeoutMs : DEFAULT_FETCH_TIMEOUT_MS;
    const controller = new AbortController();
    const timeoutId = setTimeout(function () {
      controller.abort();
    }, timeoutMs);
    const fetchOptions = Object.assign({}, opts, { signal: controller.signal });
    delete fetchOptions.timeoutMs;

    return fetch(url, fetchOptions)
      .then(function (response) {
        clearTimeout(timeoutId);
        const contentType = response.headers.get('content-type') || '';
        const isJson =
          contentType.includes('application/json') || contentType.includes('text/javascript');

        if (!response.ok) {
          const status = response.status;
          if (!isJson) {
            const err = new Error(
              '[gallery] HTTP ' +
                status +
                ' — non-JSON response (possible security challenge) fetching ' +
                url
            );
            err.status = status;
            err.isCloudflareChallenge = true;
            throw err;
          }
          return response
            .json()
            .catch(function () {
              throw new Error('[gallery] HTTP ' + status + ' fetching ' + url);
            })
            .then(function (errBody) {
              const msg = (errBody && (errBody.description || errBody.message)) || 'HTTP ' + status;
              const err = new Error('[gallery] ' + msg);
              err.status = status;
              err.shopifyError = errBody;
              throw err;
            });
        }

        if (!isJson) {
          const err = new Error(
            '[gallery] HTTP ' +
              response.status +
              ' OK but non-JSON content-type "' +
              contentType +
              '" — possible security challenge for ' +
              url
          );
          err.isCloudflareChallenge = true;
          throw err;
        }

        return response.json().catch(function (parseErr) {
          throw new Error('[gallery] Invalid JSON from ' + url + ': ' + parseErr.message);
        });
      })
      .catch(function (err) {
        clearTimeout(timeoutId);
        if (err && err.name === 'AbortError') {
          const timeoutErr = new Error('[gallery] Request timed out fetching ' + url);
          timeoutErr.isTimeout = true;
          throw timeoutErr;
        }
        throw err;
      });
  }

  /**
   * Wrap event listener to catch and log errors
   * @param {Function} handler - Event handler
   * @param {string} [context] - Log context string
   * @returns {Function} Wrapped handler
   */
  function safeEventHandler(handler, context) {
    return function wrappedHandler(e) {
      try {
        handler.call(this, e);
      } catch (err) {
        console.error('[gallery]' + (context ? ' ' + context : '') + ' error:', err);
      }
    };
  }

  // Export to global namespace
  window.GalleryUtils = {
    createRafThrottle: createRafThrottle,
    fetchJson: fetchJson,
    safeEventHandler: safeEventHandler,
  };
})();
